import { z } from 'zod/v4';
import { durationSchema, timeDashboardSchema } from './time';

export const RESOLUTION_LEVELS = ['Low', 'Medium', 'High'] as const;

export const resolutionLevelsSchema = z.enum(RESOLUTION_LEVELS);

export type QueryResolutionLevel = z.infer<typeof resolutionLevelsSchema>;

export const DEFAULT_QUERY_RESOLUTION: QueryResolutionLevel = 'Medium';

/**
 * Interval size levels for interval variables with auto enabled.
 */
export const INTERVAL_SIZE_LEVELS = ['Small', 'Medium', 'Large'] as const;

export const intervalSizeLevelsSchema = z.enum(INTERVAL_SIZE_LEVELS);

export type IntervalSizeLevel = z.infer<typeof intervalSizeLevelsSchema>;

/**
 * Default interval size for interval variables when auto_interval_size is not explicitly set.
 * Uses 'Medium' for backward compatibility with existing dashboards.
 * New interval variables and converted Grafana variables are set to 'Large', which produces
 * a larger interval value. This helps prevent rate()/increase() from showing 'No data' when the
 * interval variable is used in range vectors like rate(metric[$interval]).
 */
export const DEFAULT_INTERVAL_VARIABLE_SIZE: IntervalSizeLevel = 'Medium';

/**
 * Maps interval size levels to the internal resolution levels used for calculations.
 * The mapping is inverted: Large interval size = Low resolution (fewer data points).
 */
export const intervalSizeToResolution: Record<IntervalSizeLevel, QueryResolutionLevel> = {
  Small: 'High',
  Medium: 'Medium',
  Large: 'Low',
};

export function isValidIntervalSizeLevel(value: unknown): value is IntervalSizeLevel {
  return typeof value === 'string' && INTERVAL_SIZE_LEVELS.includes(value as IntervalSizeLevel);
}

export const QUERY_OPTIONS_URL_PARAM = 'query_options';

/**
 * Predefined resolution levels supported as well as custom duration (to influence suggested step calculation).
 */
export const resolutionSchema = z.union([resolutionLevelsSchema, durationSchema]);

export type QueryResolution = z.infer<typeof resolutionSchema>;

export const queryOptionsSchema = z.object({
  resolution: resolutionSchema.optional(),
  time: timeDashboardSchema.optional(),
});

export interface QueryOptionsDefinition extends z.TypeOf<typeof queryOptionsSchema> {}
